import React, { useState, useEffect, ReactText } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import PageTitle from "@component/PageTitle";
import { PlusCircleTwoTone, ExclamationCircleOutlined, SearchOutlined, FilterTwoTone } from "@ant-design/icons";
import { FilterDropdownProps } from "antd/lib/table/interface";
import { Button, Input, Space, Form, Modal, FormInstance, Row, Col, message } from "antd";
import SupplyChainProductPopup from "@page/SupplyChain/SupplyChainProductPopup";
import {
  fetchRetailerProductList,
  deleteRetailerProduct,
  updateSupplyChainProductSelectedIds,
  batchExportSupplyChainProduct,
  updateRetailerProductFilter,
  resetRetailerProductFilterWithRelation,
  initialState,
  updateImportErrorResults,
  fetchSupplyChainRetailList,
} from "@redux/supplyChainSlice";
import { RootState } from "@redux/rootReducer";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/store";
import {
  RetailerProductDetail,
  DeleteRetailerProductParam,
  SupplyChainProductParams,
  Currency,
  RetailerProductListParam,
} from "@api/supplyChainApi";
import { FilterWrapper, TableWrapper } from "src/styles/common";
import IntegratedTable from "@component/Table/IntegratedTable";
import ErrorResultsList from "@component/Common/ErrorResultsList";
import { PopupStateEnum } from "@constant/PopupState";
import ImportCSV from "./ImportXLS";

const Wrapper = styled.div`
  margin: 20px;
`;
const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 21px;
`;

const Edit = styled.div`
  white-space: nowrap;
  color: #1790ff;
  cursor: pointer;
`;

const Text = styled.div`
  text-align: center;
`;

const CustomSpace = styled(Space)`
  width: 100%;

  .ant-space-item {
    width: 50%;
  }
`;

const SupplyChainProductList = () => {
  const [filterForm] = Form.useForm();
  const [openPopup, setOpenPopup] = useState<PopupStateEnum | undefined>();
  const [currentData, setCurrentData] = useState<RetailerProductDetail | null>(null);
  const [showImportCSV, setShowImportCSV] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const { supplyChainId, supplyChainRelationId, currencyNumber } = useParams();
  const {
    isFetching,
    selectedSupplyChainProductSelectedIds,
    retailerProductFilter,
    retailerProductList: list,
    importErrorResults,
    supplyChainRetailList: { results: supplyChainRetailList },
  } = useSelector((state: RootState) => state.supplyChain);
  const { results: retailerProductList, count } = list;

  const retailerTaxIncluded = supplyChainRetailList.find((item) => item.id === Number(supplyChainRelationId))
    ?.taxIncluded;
  const { retailerId } = supplyChainRetailList.find((item) => item.id === Number(supplyChainRelationId)) || {};
  const handleOnCheck = (values: ReactText[]) => {
    dispatch(updateSupplyChainProductSelectedIds(values as number[]));
  };

  let currencyType;
  if (Number(currencyNumber) === Currency.TWD) {
    currencyType = "TWD";
  } else if (Number(currencyNumber) === Currency.USD) {
    currencyType = "USD";
  } else {
    currencyType = "RMB";
  }

  const handleSearch = (selectedKeys: string[], confirm: (param?: any) => void, dataIndex: any) => {
    confirm();
  };
  const handleResetSearch = (clearFilters: () => void) => {
    clearFilters();
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => {
      return (
        <div style={{ padding: "20px" }}>
          <Input
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            style={{ marginBottom: 8, display: "block" }}
          />
          <CustomSpace>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: "100%", height: 32 }}
            >
              搜尋
            </Button>
            <Button
              size="small"
              style={{ width: "100%", height: 32 }}
              onClick={() => clearFilters && handleResetSearch(clearFilters)}
            >
              重設
            </Button>
          </CustomSpace>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined, fontSize: "15px" }} />
    ),
    onFilter: (value: any, record: any) => {
      return record[dataIndex]
        ?.toString()
        .toLowerCase()
        .includes((value as string).toLowerCase());
    },
  });

  const tableColumns: any = [
    {
      title: "編號",
      dataIndex: "id",
    },
    {
      title: "SKU(品號)",
      dataIndex: "sku",
    },
    {
      title: "barcode",
      dataIndex: "barcode",
      ...getColumnSearchProps("barcode"),
    },
    {
      title: "品名",
      key: "name",
      dataIndex: "name",
    },
    {
      title: "商品售價(TWD)",
      key: "price",
      dataIndex: "price",
      render: (data: number | null) => {
        return data ? <Text>{data}</Text> : <Text>N/A</Text>;
      },
      sorter: true,
    },
    {
      title: "商品前抽(%)",
      key: "preDeductionRate",
      dataIndex: "preDeductionRate",
      render: (data: number) => {
        return <Text>{(data * 100).toFixed(3)}</Text>;
      },
    },
    {
      title: `商品報價(${currencyType})(${retailerTaxIncluded ? "含稅" : "未稅"})`,
      key: "quotedPrice",
      dataIndex: "quotedPrice",
      render: (data: number | null) => {
        return data ? <Text>{data}</Text> : <Text>N/A</Text>;
      },
      sorter: true,
    },

    {
      title: "商品後扣(%)",
      key: "postDeductionRate",
      dataIndex: "postDeductionRate",
      render: (data: number) => {
        return <Text>{(data * 100).toFixed(3)}</Text>;
      },
    },
    {
      title: "箱入數",
      key: "pcsPerCarton",
      dataIndex: "pcsPerCarton",
      render: (data: number | null) => {
        return data ? <Text>{data}</Text> : <Text>N/A</Text>;
      },
    },
    {
      title: "允收天數",
      key: "canAcceptDays",
      dataIndex: "canAcceptDays",
      render: (data: number | null) => {
        return data ? <Text>{data}</Text> : <Text>N/A</Text>;
      },
    },
    {
      title: "通路商品編號",
      key: "skuPlatform",
      dataIndex: "skuPlatform",
      render: (data: string | null) => {
        return data ? <Text>{data}</Text> : <Text>N/A</Text>;
      },
    },
    {
      title: "",
      key: "",
      dataIndex: "",
      fixed: "right",
      render: (value: any, data: any) => {
        return <Edit onClick={() => toggleEditPopup(data)}>編輯</Edit>;
      },
    },
    {
      title: "",
      key: "",
      dataIndex: "",
      fixed: "right",
      render: (data: RetailerProductDetail) => {
        const params: DeleteRetailerProductParam = {
          id: data.id,
          supplyChainRetailerProductlineRelation: Number(supplyChainRelationId),
        };
        return <Edit onClick={() => handleDelete(params)}>刪除</Edit>;
      },
    },
  ];

  const handleDelete = (params: DeleteRetailerProductParam) => {
    Modal.confirm({
      title: "你確定要刪除這些資料?",
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: "是",
      cancelText: "否",
      onOk: () => {
        dispatch(deleteRetailerProduct(params));
      },
    });
  };
  const toggleEditPopup = (data: any) => {
    setOpenPopup(PopupStateEnum.EDIT);
    setCurrentData(data);
  };

  const handleOnFilterSubmit = (params: RetailerProductListParam) => {
    const filterParams = { ...retailerProductFilter, ...params, offset: 0 };
    dispatch(updateRetailerProductFilter(filterParams));
  };

  const handleReset = () => {
    filterForm.resetFields();
    dispatch(resetRetailerProductFilterWithRelation());
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any, extra: any) => {
    if (extra.action === "sort") {
      const { field, order } = sorter;
      const snakeField = field.replace(/([A-Z])/g, "_$1").toLowerCase();
      let ordering = "";
      switch (order) {
        case "descend":
          ordering = snakeField;
          break;
        case "ascend":
          ordering = `-${snakeField}`;
          break;
        default:
          ordering = "";
      }
      const params = {
        ...retailerProductFilter,
        ordering,
        offset: 0,
      };
      dispatch(updateRetailerProductFilter(params));
    }
  };

  const togglePopup = (state: PopupStateEnum | undefined) => {
    setOpenPopup(state);
    setCurrentData(null);
  };
  const toggleImportCSV = () => {
    setShowImportCSV((prev) => !prev);
  };

  const handleExport = () => {
    if (selectedSupplyChainProductSelectedIds.length === 0) {
      message.error("請先選擇要匯出的商品");
      return;
    }
    const filter: SupplyChainProductParams = {
      productIds: selectedSupplyChainProductSelectedIds.toString(),
    };
    dispatch(batchExportSupplyChainProduct(filter));
  };

  // 取得supplyChainRelationId 存進retailerProductFilter
  useEffect(() => {
    if (supplyChainRelationId) {
      const filter = {
        ...initialState.retailerProductFilter,
        supplyChainRetailerProductlineRelation: Number(supplyChainRelationId),
      };
      dispatch(updateRetailerProductFilter(filter));
    }
  }, [dispatch, supplyChainRelationId]);

  // retailerProductFilter有supplyChainRetailerProductlineRelation,才開始取得/更新列表
  useEffect(() => {
    if (retailerProductFilter.supplyChainRetailerProductlineRelation) {
      dispatch(fetchRetailerProductList(retailerProductFilter));
    }
  }, [dispatch, retailerProductFilter]);

  useEffect(() => {
    dispatch(fetchSupplyChainRetailList({ supplyChain: Number(supplyChainId), limit: 300 }));
  }, [dispatch, supplyChainId]);

  useEffect(() => {
    if (Object.keys(importErrorResults || {}).length === 0) return;
    Modal.warning({
      title: "匯入資料有誤",
      content: <ErrorResultsList errorResult={importErrorResults as any} />,
      okText: "我知道了",
      onOk: () => {
        dispatch(updateImportErrorResults(undefined));
      },
      width: 600,
      bodyStyle: {
        height: 400,
      },
    });
  }, [dispatch, importErrorResults]);

  return (
    <Wrapper>
      <PageTitle title="供應商列表" />
      <Header>
        <Row>
          <Col>
            <Space>
              <Button type="primary" icon={<PlusCircleTwoTone />} onClick={() => togglePopup(PopupStateEnum.CREATE)}>
                新增商品
              </Button>
              {openPopup && (
                <SupplyChainProductPopup
                  currentData={currentData}
                  retailerId={retailerId}
                  onClose={() => togglePopup(undefined)}
                  mode={openPopup}
                />
              )}
              <Button icon={<FilterTwoTone />} onClick={() => setShowFilter(!showFilter)}>
                篩選
              </Button>
              <Button onClick={toggleImportCSV}>批次匯入</Button>
              <Button onClick={handleExport}>批次匯出</Button>
            </Space>
          </Col>
        </Row>
      </Header>
      <RetailerProductListFilter
        show={showFilter}
        form={filterForm}
        updateParams={handleOnFilterSubmit}
        onReset={handleReset}
      />
      <TableWrapper>
        <IntegratedTable
          isPagination
          paginationConfig={{
            updateFilterParams: (params) => dispatch(updateRetailerProductFilter(params)),
            filterParams: retailerProductFilter,
            totalCount: count,
          }}
          loading={isFetching}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          columns={tableColumns}
          dataSource={retailerProductList}
          rowSelection={{
            onChange: handleOnCheck,
            selectedRowKeys: selectedSupplyChainProductSelectedIds,
          }}
          rowKey="id"
          pagination={false}
        />
      </TableWrapper>
      {showImportCSV && <ImportCSV onClose={toggleImportCSV} />}
    </Wrapper>
  );
};

export default SupplyChainProductList;

type Props = {
  show: boolean;
  form: FormInstance;
  updateParams: (params: RetailerProductListParam) => void;
  onReset: () => void;
};
const RetailerProductListFilter = (props: Props) => {
  const { show, form, updateParams, onReset } = props;

  const handleOnReset = () => {
    onReset();
  };
  const handleOnSubmit = () => {
    const values = form.getFieldsValue();
    updateParams(values);
  };
  return (
    <FilterWrapper show={show}>
      <Form colon={false} labelAlign="left" form={form} onFinish={handleOnSubmit}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="SKU(品號)" name="sku">
              <Input type="text" allowClear />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="品名" name="name">
              <Input type="text" allowClear />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Row justify="end">
        <Col>
          <Space>
            <Button onClick={handleOnReset}>清除篩選條件</Button>
            <Button type="primary" onClick={handleOnSubmit}>
              套用
            </Button>
          </Space>
        </Col>
      </Row>
    </FilterWrapper>
  );
};
